import { useMemo } from 'react'
import { useListAssociatedPartiesForProduct } from '@/shared/hooks/api/associatedParties'
import {
  useGetMachineryDirectiveCompliance,
  useListStandardApplicability,
} from '@/shared/hooks/api/machineryDirectiveCompliance'

export type DeclarationOfConformityRequirementKey =
  | 'manufacturerAssigned'
  | 'authorisedRepresentativeAssigned'
  | 'atLeastOneStandardAssigned'
  | 'complianceStudioComplete'

export type DeclarationOfConformityRequirement = {
  key: DeclarationOfConformityRequirementKey
  met: boolean
}

export function useDeclarationOfConformityRequirements(
  complianceId: string,
  productId: string
) {
  const { data: compliance } = useGetMachineryDirectiveCompliance(complianceId)
  const { data: productAssociatedParties = [] } =
    useListAssociatedPartiesForProduct(productId)
  const { data: standards = [] } =
    useListStandardApplicability(complianceId)

  const manufacturer = useMemo(
    () =>
      productAssociatedParties.find(
        (assignment) => assignment.role === 'MANUFACTURER'
      )?.associatedParty ?? null,
    [productAssociatedParties]
  )
  const authorisedRep = useMemo(
    () =>
      productAssociatedParties.find(
        (assignment) => assignment.role === 'AUTHORISED_REPRESENTATIVE'
      )?.associatedParty ?? null,
    [productAssociatedParties]
  )

  const hasManufacturer = manufacturer != null
  const hasAuthorisedRepresentative = authorisedRep != null
  const hasAtLeastOneStandardAssigned = useMemo(
    () => standards.some((standard) => standard.isAccepted === true),
    [standards]
  )
  const isComplianceStudioComplete = compliance?.completed === true

  const requirements: DeclarationOfConformityRequirement[] = useMemo(
    () => [
      { key: 'atLeastOneStandardAssigned', met: hasAtLeastOneStandardAssigned },
      { key: 'manufacturerAssigned', met: hasManufacturer },
      { key: 'authorisedRepresentativeAssigned', met: hasAuthorisedRepresentative },
      { key: 'complianceStudioComplete', met: isComplianceStudioComplete },
    ],
    [
      hasManufacturer,
      hasAuthorisedRepresentative,
      hasAtLeastOneStandardAssigned,
      isComplianceStudioComplete,
    ]
  )

  const allRequirementsMet =
    hasManufacturer &&
    hasAuthorisedRepresentative &&
    hasAtLeastOneStandardAssigned &&
    isComplianceStudioComplete

  return {
    requirements,
    hasManufacturer,
    hasAuthorisedRepresentative,
    hasAtLeastOneStandardAssigned,
    isComplianceStudioComplete,
    allRequirementsMet,
  }
}
